import type { SteelOrderSortKeyEnum } from '@mbfab/client-api'
import { isSome } from '@mbfab/shared'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { MatchingStatusLabel, MillsheetStatusLabel, QualityCheckStatusLabel } from './status-badge'
import type { SteelOrderViewModel } from './types'

import { DataTableLink } from '@/components/app/data-table-link'
import { ColumnHelp } from '@/components/ui/data-table/column-help'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { CompactDateInput } from '@/components/ui/date-input'
import { cn } from '@/lib/utils'

// 他画面（発注詳細・紐付けダイアログ等）が引き続き './orders-table-columns' から
// import できるよう、移設先（ui/data-table/）を re-export する（本チケットの移行対象外ファイルを触らないため）
export { ColumnHelp, valueOrDash }

// 出力日は date input の value に使うため view model では ISO（YYYY-MM-DD）で保持している。
// 表示だけは他の日付列（発注データ更新日時・発行日）と同じ YYYY/MM/DD に揃える
export function outputDateOrDash(outputDate: string | null): ReactNode {
  return isSome(outputDate) ? dayjs(outputDate).format('YYYY/MM/DD') : valueOrDash(outputDate)
}

// 「参照番号」は発注照合だけのユビキタス言語（docs/domain/order_matching/README.md）で、
// 画面の文字列だけでは何の番号か分からないという FB（2026/08/03）を受けてヘルプで補う（v1 と同じ）
export const REFERENCE_NUMBER_HELP =
  '発注を一意に識別する番号です。ロール品は契約番号（デリバリー管理表）、スポット品は購買オーダ番号（SCRM）が入ります。ミルシートの注文番号がこの値と一致すると、そのミルシートが自動でこの発注に紐付きます。'

// 発注一覧・発注詳細の列ヘルプ。「何がどうなるとこの値になるのか」を書く。
// 出典は要件・ドメインドキュメント（docs/domain/order_matching/04_matching_logic.md・07_data_mapping.md）で、
// ここを直すときは必ずそちらと突き合わせること（画面の説明だけが独自に育つと仕様の二重管理になる）。
// v1 steelOrderLabels.tsx の STEEL_ORDER_COLUMN_HELP を一字一句そのまま移植する（情報構成の一部のため）
export const STEEL_ORDER_COLUMN_HELP = {
  managementNumber:
    '発注明細の管理番号を親行にまとめた表示です（重複は 1 つに、空欄は除いて並べます）。SCRM 側の「**」は未入力ではなく「本体工事」を表す正常な値です。全文は発注明細（行の展開・発注詳細）で確認できます。',
  constructionNumber:
    '発注明細の工事番号をまとめた表示です。空欄の明細は「在庫用発注」（特定の工事に紐づかない発注）を表す正常な値で、欠損ではありません。',
  constructionName: '発注明細の工事名称をまとめた表示です。空欄の明細は在庫用発注を表します。',
  // 「SCRM = スポット品」ではない。SCRM CSV にはロール品の行も混在しており、
  // 発注形態列で判別してスポット発注の生成から除外している（importScrmCsvService。
  // docs/domain/order_matching/04_matching_logic.md・05_csv_specs.md §2）。
  // ここを「ファイルの種類で決まる」と書くと誤りになるので直さないこと（2026/08/03 FB で複数回指摘）
  category:
    '発注形態による区分です。デリバリー管理表から取り込んだ発注が「ロール品」、SCRM データのうち発注形態がスポットの行から取り込んだ発注が「スポット品」です。SCRM データにはロール品の行も混在しますが、その行から発注データは作らず（ロール品はデリバリー管理表が元データ）、SCRM との突合にのみ使います。',
  millsheetStatus:
    'この発注に紐付いたミルシートの重量が、発注の合計重量に達したかを自動判定した結果です。完了 = ミルシート重量が合計重量以上（超過も完了）／未完了 = 一部だけ揃っている（分納途中など）／未登録 = 紐付いたミルシートが 1 枚もない。ミルシート・発注データの取込や紐付けの変更のたびに再判定されます。「対象外」は備品などミルシートが存在しない発注に手動で付けるフラグで、その間は自動判定しません。',
  // 一覧には isMillsheetExcluded 専用の列は無い（対象外はミルシートステータスの4値目に畳み込む）が、
  // 発注詳細のトグルUI（order-detail.tsx）はこのフラグ自体を直接操作するため、v1 と同じヘルプ文言をここに追加する
  isMillsheetExcluded:
    'この発注をミルシートステータスの自動判定の対象にするかどうかです。備品などミルシートが存在しない発注を「対象外」にすると、以後は重量照合による自動判定を行わず、ステータスは「対象外」の表示に固定されます（発注一覧では行をチェックして一括で切り替えられます）。',
  orderStatus:
    'デリバリー管理表に載っている発注が SCRM 側でも発注済みになっているかを表します。スポット品は SCRM の発注データそのものから作られるため常に「完了」です。ロール品は SCRM のロール行と管理番号 + 工事番号 + 品目番号で突き合わせ、完了 = すべてのキーで SCRM 側の重量が発注側に達した／未完了 = 一部だけ届いている（分納途中など）／未登録 = SCRM に合致する情報がまだ無い、を SCRM データの取込のたびに自動判定します。',
  qualityCheckStatus:
    '品質管理担当者によるミルシートのダブルチェックの記録です（自動判定ではありません）。発注明細ごとに完了・未完了・未登録から選んで更新します。親行はすべての明細が完了なら「完了」、すべての明細が未登録なら「未登録」、それ以外（未完了の明細を含む、または完了と未登録が混在）は「未完了」に自動算出されます。',
  outputDate:
    '鋼材ミルシート集計表を出力した日を記録しておくための項目です（自動では入りません）。発注一覧でセルをクリックして 1 件ずつ入力するほか、行をチェックして「出力日を一括変更」でまとめて入力・クリアできます。',
  supplierName:
    'SCRM の「仕入先名」です。スポット品は発注データの取込時に入ります。ロール品はデリバリー管理表に仕入先名の列が無いため、SCRM との突合で合致したロール行の仕入先名が入ります（合致するまでは空欄。複数の仕入先は「、」区切り）。',
  manufacturerName:
    '発注明細の製造会社名（デリバリー管理表の「メーカー名」）をまとめた表示です。スポット品は SCRM に取得元の列が無いため空欄になります。',
  totalWeight: '発注明細の重量の合計です。ミルシートステータスの判定はこの値とミルシート重量の比較で行います。',
  orderedWeight:
    'SCRM 側で発注済みと確認できた重量の合計です。ロール品はこの発注の照合キー（管理番号 + 工事番号 + 品目番号）に合致した SCRM ロール行の重量を合算した進捗値（発注側に届いていない分納途中の分・超過分も含む）、スポット品は SCRM の発注データそのものから作られるため合計重量と同じ値になります。',
  millsheetWeight:
    'この発注に紐付いた全ミルシートの明細重量（加工前重量）の合計です。この値が合計重量以上になるとミルシートステータスが「完了」になります。',
  importedAt: 'この発注に関する CSV（デリバリー管理表 / SCRM）が最後に取り込まれた日時です。',
} as const

export type OrderColumnKey =
  | 'managementNumbers'
  | 'constructionNumbers'
  | 'constructionNames'
  | 'category'
  | 'millsheetStatus'
  | 'orderStatus'
  | 'qualityCheckStatus'
  | 'outputDate'
  | 'supplierName'
  | 'manufacturerNames'
  | 'productNames'
  | 'standards'
  | 'totalWeight'
  | 'orderedWeight'
  | 'millsheetWeight'
  | 'importedAt'
  | 'millsheetPdf'

export type ColumnDef = {
  key: OrderColumnKey
  label: string
  numeric?: boolean
  minWidth?: string
  // 未指定の列はソート不可（v1 SORTABLE_COLUMN_KEYS に無い列と同じ）
  sortKey?: SteelOrderSortKeyEnum
  // 未指定の列はヘルプツールチップ無し（v1 STEEL_ORDER_COLUMN_HELP に無い列と同じ）
  help?: string
}

// 列の並び・項目は v1 SteelOrdersTable の18項目構成に合わせる（参照番号は左固定の別列として扱う）
export const columns: ColumnDef[] = [
  { key: 'managementNumbers', label: '管理番号', minWidth: 'min-w-28', help: STEEL_ORDER_COLUMN_HELP.managementNumber },
  {
    key: 'constructionNumbers',
    label: '工事番号',
    minWidth: 'min-w-28',
    help: STEEL_ORDER_COLUMN_HELP.constructionNumber,
  },
  {
    key: 'constructionNames',
    label: '工事名称',
    minWidth: 'min-w-64',
    help: STEEL_ORDER_COLUMN_HELP.constructionName,
  },
  { key: 'category', label: '分類', minWidth: 'min-w-24', sortKey: 'category', help: STEEL_ORDER_COLUMN_HELP.category },
  {
    key: 'millsheetStatus',
    label: 'ミルシートステータス',
    minWidth: 'min-w-48',
    sortKey: 'millsheetStatus',
    help: STEEL_ORDER_COLUMN_HELP.millsheetStatus,
  },
  {
    key: 'orderStatus',
    label: '発注ステータス',
    minWidth: 'min-w-32',
    sortKey: 'orderStatus',
    help: STEEL_ORDER_COLUMN_HELP.orderStatus,
  },
  {
    key: 'qualityCheckStatus',
    label: '品管照合ステータス',
    minWidth: 'min-w-40',
    sortKey: 'qualityCheckStatus',
    help: STEEL_ORDER_COLUMN_HELP.qualityCheckStatus,
  },
  {
    key: 'outputDate',
    label: '出力日',
    minWidth: 'min-w-28',
    sortKey: 'outputDate',
    help: STEEL_ORDER_COLUMN_HELP.outputDate,
  },
  {
    key: 'supplierName',
    label: 'サプライヤー名',
    minWidth: 'min-w-56',
    sortKey: 'supplierName',
    help: STEEL_ORDER_COLUMN_HELP.supplierName,
  },
  {
    key: 'manufacturerNames',
    label: '製造会社名',
    minWidth: 'min-w-32',
    help: STEEL_ORDER_COLUMN_HELP.manufacturerName,
  },
  { key: 'productNames', label: '品名', minWidth: 'min-w-36' },
  { key: 'standards', label: '規格', minWidth: 'min-w-28' },
  {
    key: 'totalWeight',
    label: '合計重量(kg)',
    numeric: true,
    minWidth: 'min-w-32',
    sortKey: 'totalWeight',
    help: STEEL_ORDER_COLUMN_HELP.totalWeight,
  },
  {
    key: 'orderedWeight',
    label: '発注済み重量(kg)',
    numeric: true,
    minWidth: 'min-w-36',
    sortKey: 'orderedWeight',
    help: STEEL_ORDER_COLUMN_HELP.orderedWeight,
  },
  {
    key: 'millsheetWeight',
    label: 'ミルシート重量(kg)',
    numeric: true,
    minWidth: 'min-w-36',
    sortKey: 'millsheetWeight',
    help: STEEL_ORDER_COLUMN_HELP.millsheetWeight,
  },
  {
    key: 'importedAt',
    label: '発注データ更新日時',
    minWidth: 'min-w-44',
    sortKey: 'importedAt',
    help: STEEL_ORDER_COLUMN_HELP.importedAt,
  },
  { key: 'millsheetPdf', label: 'ミルシート', minWidth: 'min-w-32' },
]

// 出力日のセル内編集（v1 SteelOrderOutputDateInput と同じ意味論: クリックで日付入力を出し、
// 確定で updateSteelOrders({ ids: [id], outputDate }) を呼ぶ。空にすればクリアできる）。
// 一括変更（複数 id）と同じ mutation を ids: [order.id] で呼ぶだけなので、呼び出しは
// route 側の onUpdateOutputDate（ids 配列を受ける）に一本化している
function OutputDateCell({
  order,
  onUpdateOutputDate,
}: {
  order: SteelOrderViewModel
  onUpdateOutputDate: (ids: string[], outputDate: string | null) => Promise<boolean>
}) {
  const [editing, setEditing] = useState(false)
  const [draftValue, setDraftValue] = useState(order.outputDate ?? '')
  const [failed, setFailed] = useState(false)

  const commit = () => {
    setEditing(false)
    const value = draftValue === '' ? null : draftValue
    // 未変更のまま blur / Enter された場合まで更新APIを呼ぶと無駄な通信になるため、
    // 実際に値が変わったときだけ呼び出す
    if (value === (order.outputDate ?? null)) {
      return
    }
    void onUpdateOutputDate([order.id], value).then((ok) => {
      setFailed(!ok)
    })
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setFailed(false)
          setDraftValue(order.outputDate ?? '')
          setEditing(true)
        }}
        aria-label={`${order.referenceNumber}の出力日を編集`}
        data-testid={`steel-order-output-date-${order.id}`}
        data-failed={failed}
        className={cn('-mx-1 rounded px-1 py-0.5 text-left hover:bg-muted', failed && 'text-destructive')}
      >
        {outputDateOrDash(order.outputDate)}
        {failed && <span className="ml-1 text-xs">更新失敗</span>}
      </button>
    )
  }

  return (
    <CompactDateInput
      autoFocus
      value={draftValue}
      aria-label={`${order.referenceNumber}の出力日`}
      data-testid={`steel-order-output-date-input-${order.id}`}
      className="h-7 rounded px-1"
      wrapperClassName="max-w-40"
      onChange={(event) => {
        // ネイティブ date input は打鍵ごとに change が発火し、既存値のある年欄をキー入力中は
        // "0002-08-20" のような一時的に妥当な（が意図しない）日付を返す。カレンダー選択との
        // 区別も API 上できないため、ここでは即確定・即クローズせず state を更新するだけにする
        // （確定は onBlur / Enter に寄せる。v1 の antd DatePicker は確定時のみ onChange が発火していた）
        setDraftValue(event.target.value)
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        // Enter による確定（blur）は DateInput 自身が担うため、ここでは重複させない。
        // 「Escape で編集前の値に戻す」はこのセル固有の挙動なのでここに残す。
        // DateInput 側で開いているカレンダーだけを閉じる処理より先に伝播を止め、編集自体を終了する
        if (event.key === 'Escape') {
          event.preventDefault()
          setDraftValue(order.outputDate ?? '')
          setEditing(false)
        }
      }}
    />
  )
}

export function renderCell(
  col: ColumnDef,
  order: SteelOrderViewModel,
  context: {
    onUpdateOutputDate: (ids: string[], outputDate: string | null) => Promise<boolean>
  },
): ReactNode {
  switch (col.key) {
    case 'managementNumbers':
      return valueOrDash(order.managementNumbers)
    case 'constructionNumbers':
      return valueOrDash(order.constructionNumbers)
    case 'constructionNames':
      return (
        <span className="block max-w-[22rem] truncate" title={order.constructionNames}>
          {valueOrDash(order.constructionNames)}
        </span>
      )
    case 'category':
      // 分類は正常/異常のような意味を持たない属性なので、素の文字で出す（v1 と同じ）
      return <span className="text-muted-foreground">{order.category === 'roll' ? 'ロール品' : 'スポット品'}</span>
    case 'millsheetStatus':
      return (
        <span className="inline-flex items-baseline gap-2">
          {/* 不足量は millsheet-status の testid の外に出す。中に入れると
              e2e の toHaveText('未完了') が「未完了 −450」になって落ちる（v1 と同じ理由） */}
          <span data-testid={`millsheet-status-${order.referenceNumber}`}>
            <MillsheetStatusLabel
              millsheetStatus={order.millsheetStatus}
              isMillsheetExcluded={order.isMillsheetExcluded}
            />
          </span>
          {isSome(order.millsheetShortfallKg) && (
            // 不足は異常ではなく「未完了」の理由なので、警告の赤ではなく
            // 未完了ステータスと同系のアンバーで出す（赤は削除など取り消せない操作に残す。v1 と同じ理由）
            <span
              className="text-xs tabular-nums text-warning"
              data-testid={`millsheet-weight-shortage-${order.referenceNumber}`}
            >
              −{order.millsheetShortfallKg.toLocaleString()}
            </span>
          )}
        </span>
      )
    case 'orderStatus':
      return (
        <span data-testid={`order-status-${order.referenceNumber}`}>
          <MatchingStatusLabel status={order.orderStatus} />
        </span>
      )
    case 'qualityCheckStatus':
      return (
        <span data-testid={`quality-check-status-${order.referenceNumber}`}>
          <QualityCheckStatusLabel status={order.qualityCheckStatus} />
        </span>
      )
    case 'outputDate':
      return <OutputDateCell order={order} onUpdateOutputDate={context.onUpdateOutputDate} />
    case 'supplierName':
      return (
        <span
          className="block max-w-[22rem] truncate"
          title={order.supplierName ?? undefined}
          data-testid={`supplier-name-${order.referenceNumber}`}
        >
          {valueOrDash(order.supplierName)}
        </span>
      )
    case 'manufacturerNames':
      return valueOrDash(order.manufacturerNames)
    case 'productNames':
      return valueOrDash(order.productNames)
    case 'standards':
      return valueOrDash(order.standards)
    case 'totalWeight':
      return <span className="tabular-nums">{order.totalWeight.toLocaleString()}</span>
    case 'orderedWeight':
      return (
        <span className="tabular-nums" data-testid={`ordered-weight-${order.referenceNumber}`}>
          {order.orderedWeight.toLocaleString()}
        </span>
      )
    case 'millsheetWeight':
      return <span className="tabular-nums">{order.millsheetWeight.toLocaleString()}</span>
    case 'importedAt': {
      // DateTime は UTC で返るため JST を明示して表示する（v1 と同じ）
      const importedAt = dayjs(order.importedAt).tz('Asia/Tokyo')
      return (
        <span className="whitespace-nowrap tabular-nums">
          {importedAt.format('YYYY/MM/DD')} <span className="text-muted-foreground">{importedAt.format('HH:mm')}</span>
        </span>
      )
    }
    case 'millsheetPdf':
      if (order.millsheets.length === 0) {
        return valueOrDash(null)
      }
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {order.millsheets.map((millsheet) => (
            <DataTableLink
              key={millsheet.id}
              to="/t/$tenant_id/millsheets/$millsheet_id"
              params={{ tenant_id: order.tenantId, millsheet_id: millsheet.id }}
            >
              {millsheet.no}
            </DataTableLink>
          ))}
        </div>
      )
  }
}
